/**
 * Local storage utility functions
 * Validates: Requirements 12.7, 12.8
 */

import type { ChatMessage, LocalStorageChatData, LocalStorageTestData } from '../types';

// Local storage keys
export const STORAGE_KEYS = {
  CHAT: (lectureId: string) => `elive_chat_${lectureId}`,
  TEST: (lectureId: string, userId: string) => `elive_test_${lectureId}_${userId}`,
  UI_STATE: 'elive_ui_state',
} as const;

/**
 * Save chat messages to local storage
 * @param lectureId - Lecture ID
 * @param messages - Array of chat messages
 */
export function saveChatMessages(lectureId: string, messages: ChatMessage[]): void {
  try {
    const data: LocalStorageChatData = {
      lectureId,
      messages,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEYS.CHAT(lectureId), JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save chat messages to localStorage:', error);
  }
}

/**
 * Load chat messages from local storage
 * @param lectureId - Lecture ID
 * @returns Array of chat messages or empty array if not found
 */
export function loadChatMessages(lectureId: string): ChatMessage[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.CHAT(lectureId));
    if (!stored) {
      return [];
    }
    
    const data: LocalStorageChatData = JSON.parse(stored);
    return data.messages || [];
  } catch (error) {
    console.error('Failed to load chat messages from localStorage:', error);
    return [];
  }
}

/**
 * Add a single chat message to local storage
 * @param lectureId - Lecture ID
 * @param message - Chat message to add
 */
export function addChatMessage(lectureId: string, message: ChatMessage): void {
  const messages = loadChatMessages(lectureId);
  messages.push(message);
  saveChatMessages(lectureId, messages);
}

/**
 * Clear chat messages for a specific lecture
 * @param lectureId - Lecture ID
 */
export function clearChatMessages(lectureId: string): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.CHAT(lectureId));
  } catch (error) {
    console.error('Failed to clear chat messages from localStorage:', error);
  }
}

/**
 * Save test answer to local storage
 * @param lectureId - Lecture ID
 * @param userId - User ID
 * @param questionId - Question ID
 * @param choiceId - Selected choice ID
 */
export function saveTestAnswer(
  lectureId: string,
  userId: string,
  questionId: number,
  choiceId: number | null
): void {
  try {
    const data = loadTestData(lectureId, userId);
    
    // Remove existing answer for this question if any
    const filteredAnswers = data.answers.filter(a => a.questionId !== questionId);
    
    // Add new answer
    filteredAnswers.push({
      questionId,
      choiceId,
      submittedAt: new Date().toISOString(),
    });
    
    const updatedData: LocalStorageTestData = {
      lectureId,
      answers: filteredAnswers,
      lastUpdated: new Date().toISOString(),
    };
    
    localStorage.setItem(STORAGE_KEYS.TEST(lectureId, userId), JSON.stringify(updatedData));
  } catch (error) {
    console.error('Failed to save test answer to localStorage:', error);
  }
}

/**
 * Load test data from local storage
 * @param lectureId - Lecture ID
 * @param userId - User ID
 * @returns Test data or empty data structure if not found
 */
export function loadTestData(lectureId: string, userId: string): LocalStorageTestData {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.TEST(lectureId, userId));
    if (!stored) {
      return {
        lectureId,
        answers: [],
        lastUpdated: new Date().toISOString(),
      };
    }
    
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load test data from localStorage:', error);
    return {
      lectureId,
      answers: [],
      lastUpdated: new Date().toISOString(),
    };
  }
}

/**
 * Get test answer for a specific question
 * @param lectureId - Lecture ID
 * @param userId - User ID
 * @param questionId - Question ID
 * @returns Choice ID or null if not found
 */
export function getTestAnswer(lectureId: string, userId: string, questionId: number): number | null {
  const data = loadTestData(lectureId, userId);
  const answer = data.answers.find(a => a.questionId === questionId);
  if (!answer) return null;
  return answer.choiceId;
}

/**
 * Remove a specific test answer from local storage
 * @param lectureId - Lecture ID
 * @param userId - User ID
 * @param questionId - Question ID to remove
 */
export function removeTestAnswer(lectureId: string, userId: string, questionId: number): void {
  try {
    const data = loadTestData(lectureId, userId);
    const filteredAnswers = data.answers.filter(a => a.questionId !== questionId);

    const updatedData: LocalStorageTestData = {
      lectureId,
      answers: filteredAnswers,
      lastUpdated: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEYS.TEST(lectureId, userId), JSON.stringify(updatedData));
  } catch (error) {
    console.error('Failed to remove test answer from localStorage:', error);
  }
}

/**
 * Clear test data for a specific lecture and user
 * @param lectureId - Lecture ID
 * @param userId - User ID
 */
export function clearTestData(lectureId: string, userId: string): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.TEST(lectureId, userId));
  } catch (error) {
    console.error('Failed to clear test data from localStorage:', error);
  }
}

/**
 * Save UI state to local storage
 * @param state - UI state object
 */
export function saveUIState(state: Record<string, unknown>): void {
  try {
    localStorage.setItem(STORAGE_KEYS.UI_STATE, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save UI state to localStorage:', error);
  }
}

/**
 * Load UI state from local storage
 * @returns UI state object or empty object if not found
 */
export function loadUIState(): Record<string, unknown> {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.UI_STATE);
    if (!stored) {
      return {};
    }
    
    return JSON.parse(stored);
  } catch (error) {
    console.error('Failed to load UI state from localStorage:', error);
    return {};
  }
}

/** 講義データ（チャット・回答・問題）の localStorage 保持期間 */
const LECTURE_DATA_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30日

/**
 * 保持期間を過ぎた講義データを localStorage から削除する（STU-12対応）。
 *
 * elive_chat_* / elive_test_* / elive_questions_* は講義・ユーザーごとにキーが増え、
 * 明示的な削除処理がないため無期限に蓄積されていた。視聴ページ表示時に呼び出し、
 * 最終更新（lastUpdated / meta.generated_at）から30日を超えたエントリを削除する。
 * 回答データはサーバー（/answers/history）にも保存されており、削除後も復元可能。
 * UI設定（elive_ui_state）は講義に紐付かないため対象外とする。
 */
export function cleanupExpiredLectureData(maxAgeMs: number = LECTURE_DATA_MAX_AGE_MS): void {
  try {
    const now = Date.now();
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (!key.startsWith('elive_')) return;
      if (key === STORAGE_KEYS.UI_STATE) return;
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return;
        const data = JSON.parse(raw) as {
          lastUpdated?: string;
          meta?: { generated_at?: string };
        };
        const updatedAt = data?.lastUpdated ?? data?.meta?.generated_at;
        // タイムスタンプが判定できないデータは誤削除を避けるため残す
        if (!updatedAt) return;
        const updatedAtMs = new Date(updatedAt).getTime();
        if (Number.isFinite(updatedAtMs) && now - updatedAtMs > maxAgeMs) {
          localStorage.removeItem(key);
          console.log('[localStorage] 保持期間を過ぎた講義データを削除:', key);
        }
      } catch {
        // パースできない破損データは削除する
        localStorage.removeItem(key);
        console.warn('[localStorage] 破損した講義データを削除:', key);
      }
    });
  } catch (error) {
    console.error('Failed to cleanup expired lecture data from localStorage:', error);
  }
}

/**
 * Clear all student-viewer data from local storage
 */
export function clearAllData(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('elive_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Failed to clear all data from localStorage:', error);
  }
}
